import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, LinearProgress, Button, ButtonBase, Collapse, Chip, CircularProgress, Alert, IconButton, Tooltip,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useShell } from '../components/Layout';
import { isMember } from '../utils/access';
import { syncApi } from '../services/api';
import { toIso, fromIso } from '../components/common/PeriodPicker';
import { AUTO_HISTORY_FROM, EARLIEST_DATE } from '../utils/period';
import MarketplaceIcon from '../components/common/MarketplaceIcon';
import { ErrorState } from '../components/common/States';
import { formatDate, formatNumber, formatTimeMsk, formatRelativeMsk, apiError, addDays } from '../utils/format';
import { tokens, marketplaceMeta } from '../theme';
import { hasSync } from '../utils/marketplaces';

const STATUS = {
  ready: { label: 'Готово', color: tokens.success, icon: <CheckCircleRoundedIcon sx={{ color: tokens.success }} /> },
  running: { label: 'Загружается', color: tokens.primary, icon: <CircularProgress size={20} thickness={5} /> },
  pending: { label: 'В очереди', color: tokens.inkMuted, icon: <ScheduleRoundedIcon sx={{ color: tokens.inkMuted }} /> },
  error: { label: 'Ошибка', color: tokens.error, icon: <ErrorRoundedIcon sx={{ color: tokens.error }} /> },
  no_access: { label: 'Нет доступа', color: tokens.error, icon: <LockOutlinedIcon sx={{ color: tokens.error }} /> },
  unavailable: { label: 'Недоступно', color: tokens.inkMuted, icon: <LockOutlinedIcon sx={{ color: tokens.inkMuted }} /> },
};

const stageStatus = (s) => {
  if (s.status === 'ready' || (s.initial_done && s.status !== 'no_access' && s.status !== 'error')) return 'ready';
  if (s.status === 'pending' && s.items === 0 && !s.chunks_done) return 'pending';
  if (s.status === 'pending') return 'running';
  return s.status;
};

// Sources that hold only a current snapshot: no date coverage or history notes for them.
const SNAPSHOT_KEYS = ['wb_stocks', 'wb_promotions', 'ozon_stocks'];

// One loading stage: a narrow row (status, name, short progress); a click opens the details.
const Stage = ({ s, onRetry, retrying }) => {
  const [open, setOpen] = useState(false);
  const st = stageStatus(s);
  const meta = STATUS[st] || STATUS.pending;
  const hasChunks = s.chunks_total != null && s.chunks_total > 0;
  const pct = hasChunks ? Math.min(100, (s.chunks_done / s.chunks_total) * 100) : 0;
  const partialHistory = s.coverage_from && s.coverage_from > s.target_from && !s.backfilling;
  const operational = s.key === 'wb_sales' || s.key === 'wb_orders';
  const showBar = (st !== 'ready' && hasChunks) || (s.backfilling && hasChunks);
  return (
    <Box sx={{ borderBottom: `1px solid ${tokens.border}`, '&:last-child': { borderBottom: 'none' } }}>
      <ButtonBase onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label={`${s.label}: ${meta.label}. Подробнее`}
        sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1, borderRadius: 1.5, textAlign: 'left',
          '&:hover': { bgcolor: tokens.primaryTint } }}>
        <Box sx={{ width: 20, display: 'flex', justifyContent: 'center', '& svg': { fontSize: 20 } }}>
          {st === 'running' ? <CircularProgress size={16} thickness={5} /> : meta.icon}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0, overflow: { sm: 'hidden' }, textOverflow: { sm: 'ellipsis' }, whiteSpace: { sm: 'nowrap' } }}>{s.label}</Typography>
        {showBar && (
          <Box sx={{ width: 120, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
            <LinearProgress variant="determinate" value={pct} sx={{ height: 4, borderRadius: 2 }}
              aria-label={`${s.label}: загружено ${s.chunks_done} из ${s.chunks_total}`} />
          </Box>
        )}
        <Typography variant="caption" sx={{ color: meta.color, fontWeight: 600, flexShrink: 0, minWidth: { sm: 92 }, textAlign: 'right' }}>
          {showBar ? `${s.chunks_done}/${s.chunks_total}` : meta.label}
        </Typography>
        <ExpandMoreRoundedIcon fontSize="small" sx={{ color: tokens.inkMuted, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
      <Box sx={{ pl: { xs: 1, sm: 5 }, pr: 1, pb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {s.description}
          {showBar && <Box component="span" sx={{ color: meta.color, fontWeight: 600 }}>{` · ${meta.label}`}</Box>}
        </Typography>

        {st !== 'ready' && hasChunks && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {s.key === 'wb_finance' || s.key === 'ozon_realization' ? 'Отчётов загружено' : 'Частей загружено'}: {s.chunks_done} из {s.chunks_total}
          </Typography>
        )}
        {(s.backfilling || s.history_from) && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: tokens.primaryDark, fontWeight: 600 }}>
            {s.history_from ? `В очереди: история с ${formatDate(s.history_from)}` : `Догружаем историю с ${formatDate(s.target_from)}`}
            {s.backfilling && hasChunks ? ` · ${s.chunks_done} из ${s.chunks_total}` : ''}
          </Typography>
        )}
        {(st === 'running' || st === 'pending') && !hasChunks && s.items > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Обработано записей: {formatNumber(s.items)} (общий объём заранее неизвестен)
          </Typography>
        )}

        {s.key === 'wb_stocks' && st === 'ready' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            WB отдаёт только текущие остатки, а не историю. MARQIT сохраняет снимок каждые 6 часов, поэтому динамика остатков копится с момента подключения.
            {s.last_success_at ? ` Обновлено ${formatRelativeMsk(s.last_success_at)}.` : ''}
          </Typography>
        )}
        {s.key === 'wb_promotions' && st === 'ready' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Текущие и ближайшие акции WB, без истории прошедших. Список обновляется каждые 6 часов.
            {s.last_success_at ? ` Обновлено ${formatRelativeMsk(s.last_success_at)}.` : ''}
          </Typography>
        )}
        {!SNAPSHOT_KEYS.includes(s.key) && s.coverage_from && s.coverage_to && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Данные с {formatDate(s.coverage_from)} по {formatDate(s.coverage_to)}
            {s.last_success_at ? ` · обновлено ${formatRelativeMsk(s.last_success_at)}` : ''}
          </Typography>
        )}
        {partialHistory && st === 'ready' && !SNAPSHOT_KEYS.includes(s.key) && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: tokens.warning }}>
            {operational
              ? `WB хранит оперативные данные около 90 дней, поэтому они есть только с ${formatDate(s.coverage_from)}. Новые дни MARQIT сохраняет у себя; за более ранние даты продажи и возвраты есть в финансовых отчётах.`
              : `WB отдал данные только с ${formatDate(s.coverage_from)}: более ранних у WB нет.`}
          </Typography>
        )}

        {(st === 'error' || st === 'no_access') && (
          <Alert severity={st === 'no_access' ? 'warning' : 'error'} sx={{ mt: 1.5 }}
            action={s.can_retry && (
              <Button color="inherit" size="small" startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : <ReplayRoundedIcon />}
                onClick={() => onRetry(s)} disabled={retrying}>
                Повторить
              </Button>
            )}>
            {s.last_error || 'Не удалось загрузить данные'}
            {st === 'no_access' && (
              <Box sx={{ mt: 0.5 }}>
                {s.key === 'wb_finance' && /additional requirements/i.test(s.last_error || '')
                  ? 'Финансовый раздел WB не открывается базовым токеном, даже если отмечена категория «Финансы». Создайте в кабинете WB персональный или сервисный токен с категорией «Финансы» и обновите его в настройках подключения.'
                  : s.category
                    ? `Добавьте токену категорию «${s.category}» в личном кабинете WB и обновите токен в настройках подключения.`
                    : 'Выдайте API-ключу роль с доступом к этому разделу в личном кабинете Ozon (Настройки → Seller API) и обновите ключ в настройках подключения.'}
              </Box>
            )}
            {st === 'error' && s.next_run_at && (
              <Box sx={{ mt: 0.5 }}>Автоматический повтор в {formatTimeMsk(s.next_run_at)}.</Box>
            )}
          </Alert>
        )}
        {s.last_error_code === 'rate_limited' && st !== 'error' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {s.key.startsWith('ozon_') ? 'Ozon' : 'WB'} ограничил частоту запросов — продолжим в {formatTimeMsk(s.next_run_at)}.
          </Typography>
        )}
      </Box>
      </Collapse>
    </Box>
  );
};

// Loaded history range and a request for older data (queued, loaded within WB limits).
const HistoryCard = ({ accountId, sources, onRequested }) => {
  const history = sources.filter((s) => s.has_history);
  // where each source's history will start once queued requests are applied
  const starts = history.map((s) => s.history_from || s.target_from).sort();
  const requestedFrom = starts[0];
  const latestFrom = starts[starts.length - 1];
  const pending = history.some((s) => s.history_from || s.backfilling);
  const [date, setDate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  if (!history.length) return null;
  const from = toIso(date);
  const invalid = !from || from < EARLIEST_DATE || from >= latestFrom;
  const submit = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const { data } = await syncApi.requestHistory(accountId, from);
      setMessage(data.sources > 0
        ? { severity: 'success', text: `Запрос принят: загрузим данные с ${formatDate(from)}. Это займёт от нескольких часов до суток — страницу можно закрыть.` }
        : { severity: 'info', text: `Данные с ${formatDate(from)} уже загружены или запрошены.` });
      setDate(null);
      onRequested();
    } catch (e) {
      setMessage({ severity: 'error', text: apiError(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 3 }} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <HistoryRoundedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>История данных</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {requestedFrom ? `Загружается с ${formatDate(requestedFrom)}.` : `Загружается с ${formatDate(AUTO_HISTORY_FROM)}.`}
              {' '}Нужны более ранние данные — выберите дату.
              {pending && ' Догрузка уже идёт.'}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ flexShrink: 0 }}>
            <DatePicker label="С даты" value={date} onChange={setDate} minDate={fromIso(EARLIEST_DATE)}
              maxDate={fromIso(addDays(latestFrom, -1))} disableFuture
              slotProps={{ textField: { size: 'small', sx: { width: { sm: 170 } } } }} />
            <Button variant="outlined" onClick={submit} disabled={invalid || busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <HistoryRoundedIcon />}>
              Загрузить
            </Button>
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          WB отдаёт финансовые отчёты с {formatDate(EARLIEST_DATE)}, оперативные продажи и заказы — примерно за 90 дней.
        </Typography>
        {message && <Alert severity={message.severity} sx={{ mt: 1.5 }}>{message.text}</Alert>}
      </CardContent>
    </Card>
  );
};

// Cabinets of the same marketplace: switch between their loading pages.
const AccountSwitcher = ({ accounts, currentId, marketplace, onAdd }) => (
  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" role="tablist" aria-label="Кабинеты">
    {accounts.map((a) => {
      const active = a.id === currentId;
      return (
        <ButtonBase key={a.id} component={RouterLink} to={`/marketplaces/sync/${a.id}`} role="tab" aria-selected={active}
          sx={{ gap: 1, pl: 0.5, pr: 1.5, py: 0.5, borderRadius: 999, maxWidth: 240,
            bgcolor: active ? tokens.paper : 'rgba(255,255,255,0.45)',
            border: `1px solid ${active ? tokens.primary : 'transparent'}`,
            boxShadow: active ? '0 1px 3px rgba(16,24,40,0.08)' : 'none',
            '&:hover': { bgcolor: tokens.paper } }}>
          <MarketplaceIcon marketplace={a.marketplace} size={24} />
          <Typography variant="body2" noWrap sx={{ fontWeight: active ? 700 : 500, color: active ? 'text.primary' : 'text.secondary' }}>
            {a.name}
          </Typography>
        </ButtonBase>
      );
    })}
    {onAdd && (
      <ButtonBase onClick={() => onAdd(marketplace)} aria-label="Добавить кабинет"
        sx={{ gap: 0.5, px: 1.5, py: 0.75, borderRadius: 999, border: `1px dashed ${tokens.primary}`, color: tokens.primary,
          '&:hover': { bgcolor: tokens.paper } }}>
        <AddRoundedIcon fontSize="small" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Кабинет</Typography>
      </ButtonBase>
    )}
  </Stack>
);

const SyncStatusPage = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { profile, openConnect } = useShell() || {};
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [retrying, setRetrying] = useState('');
  const [retryError, setRetryError] = useState('');
  // the cabinet whose data the page shows; an answer for a previous cabinet is dropped
  const current = useRef(accountId);
  current.current = accountId;

  const load = useCallback(async () => {
    try {
      const { data: d } = await syncApi.status(accountId);
      if (current.current !== accountId) return;
      setData({ ...d, accountId });
      setError('');
    } catch (e) {
      if (current.current !== accountId) return;
      setError(e?.response?.status === 404 ? 'Кабинет не найден или принадлежит другому пользователю.' : apiError(e));
    }
  }, [accountId]);

  useEffect(() => {
    setData(null);
    setError('');
    setRetryError('');
    load();
  }, [load]);

  // poll only while something can still change: a loaded cabinet with stages not finished yet
  const settled = Boolean(error) || (data?.accountId === accountId && (
    !hasSync(data.accounts.find((a) => a.id === accountId)?.marketplace)
    || (data.sources || []).every((s) => stageStatus(s) === 'ready' && !s.backfilling && !s.history_from)));
  useEffect(() => {
    if (settled) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load, settled]);

  const retry = async (s) => {
    setRetrying(s.key);
    setRetryError('');
    try {
      await syncApi.retry(accountId, s.key);
      await load();
    } catch (e) {
      setRetryError(apiError(e));
    } finally {
      setRetrying('');
    }
  };

  if (error) return <Box sx={{ maxWidth: 1480, mx: 'auto' }}><ErrorState message={error} onRetry={load} /></Box>;
  if (!data || data.accountId !== accountId) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  const account = data.accounts.find((a) => a.id === accountId);
  const meta = marketplaceMeta(account?.marketplace);
  const sameMp = data.accounts.filter((a) => a.marketplace === account?.marketplace);
  const sources = data.sources || [];
  const done = sources.filter((s) => stageStatus(s) === 'ready').length;
  const failed = sources.filter((s) => ['error', 'no_access'].includes(stageStatus(s))).length;
  const allDone = sources.length > 0 && done === sources.length;
  const isOzon = account?.marketplace === 'ozon';
  const financeReady = sources.find((s) => s.key === (isOzon ? 'ozon_realization' : 'wb_finance'))?.initial_done;
  const canAdd = openConnect && !isMember(profile);

  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
      <Card sx={{ mb: 2, background: tokens.heroGradient }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
          <AccountSwitcher accounts={sameMp} currentId={accountId} marketplace={account?.marketplace} onAdd={canAdd ? openConnect : null} />

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
            <MarketplaceIcon marketplace={account?.marketplace} size={36} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h2" component="h1" sx={{ fontSize: { xs: '1.2rem', md: '1.4rem' }, lineHeight: 1.25 }}>
                {allDone ? 'Данные загружены' : 'Загружаем данные'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>{meta.label} · {account?.name}</Typography>
            </Box>
          </Stack>

          {!hasSync(account?.marketplace) ? (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Для {meta.label} автоматическая загрузка данных ещё не реализована. Подключение сохранено и проверено; аналитика появится после запуска интеграции.
            </Alert>
          ) : (
            <>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 3 }} alignItems={{ md: 'center' }} sx={{ mt: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <LinearProgress variant="determinate" value={sources.length ? (done / sources.length) * 100 : 0} sx={{ height: 6, borderRadius: 3 }}
                    aria-label={`Готово этапов: ${done} из ${sources.length}`} />
                  <Stack direction="row" spacing={2} sx={{ mt: 0.75 }} flexWrap="wrap">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Готово этапов: {done} из {sources.length}</Typography>
                    {failed > 0 && <Typography variant="body2" sx={{ color: tokens.error, fontWeight: 600 }}>С ошибками: {failed}</Typography>}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }} flexWrap="wrap" useFlexGap>
                  <Button variant="contained" size="small" endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/mp/${account.marketplace}?account=${accountId}`)}>
                    {financeReady ? 'К аналитике' : 'Доступная аналитика'}
                  </Button>
                  <Button variant="outlined" size="small" component={RouterLink} to="/dashboard">На главную</Button>
                </Stack>
              </Stack>
              {(!allDone || !financeReady) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {!allDone && `${isOzon ? 'Ozon' : 'WB'} ограничивает частоту запросов, поэтому загрузка может занять несколько часов. Страницу можно закрыть — она продолжится на сервере.`}
                  {!allDone && !financeReady && ' '}
                  {!financeReady && 'Пока идут финансовые отчёты, суммы предварительные.'}
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {account?.marketplace === 'wildberries' && !isMember(profile) && <HistoryCard key={accountId} accountId={accountId} sources={sources} onRequested={load} />}

      {sources.length > 0 && (
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 1, md: 1.5 } } }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, mr: 0.5 }}>Этапы загрузки</Typography>
              {['ready', 'running', 'pending', 'error', 'no_access'].map((k) => {
                const n = sources.filter((s) => stageStatus(s) === k).length;
                return n ? <Chip key={k} size="small" label={`${STATUS[k].label}: ${n}`} sx={{ color: STATUS[k].color, bgcolor: tokens.primaryTint }} /> : null;
              })}
              <Box sx={{ flex: 1 }} />
              <Tooltip title={expanded ? 'Свернуть' : 'Развернуть'}>
                <IconButton size="small" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded} aria-label="Показать этапы">
                  <ExpandMoreRoundedIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
                </IconButton>
              </Tooltip>
            </Stack>
            {retryError && <Alert severity="error" sx={{ mt: 1.5 }}>{retryError}</Alert>}
            <Collapse in={expanded}>
              <Box sx={{ mt: 1 }}>
                {sources.map((s) => <Stage key={`${accountId}:${s.key}`} s={s} onRetry={retry} retrying={retrying === s.key} />)}
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SyncStatusPage;
